import React from 'react';
import { cn } from '../../../lib/cn';

/**
 * Chip, the 44px filter pill.
 *
 * CSS selector: `mobile/atoms.css` `.ds-chip` (+ `--active`, `--display`).
 *
 * Doctrine: a filter chip is a command, because tapping it changes the result
 * set, so the selected state is an ink invert and never an accent fill. This is
 * the one place selection reads as ink rather than accent, and it is deliberate.
 *
 * `aria-pressed` is set from the same `variant` value that drives the class, so
 * the visual state and the announced state can never drift apart. The CSS
 * accepts either hook.
 *
 * `display` is the inert decorative chip. It renders a span, so it stays out of
 * the tab order and implies no affordance it does not have.
 */
const CHIP_VARIANT = {
  default: null,
  active: 'ds-chip--active',
  display: 'ds-chip--display',
};

export const Chip = React.forwardRef(function Chip(
  { className, variant = 'default', type = 'button', disabled, ...rest },
  ref,
) {
  if (variant === 'display') {
    return <span className={cn('ds-chip', CHIP_VARIANT.display, className)} {...rest} />;
  }

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      aria-pressed={variant === 'active'}
      className={cn('ds-chip', CHIP_VARIANT[variant], className)}
      {...rest}
    />
  );
});

export default Chip;
